/**
 * The one ⌘Z slot (D85, spec §8.1): the latest undo offer anywhere in the app holds it, and one keydown listener serves
 * every offerer. A newer claim by another owner supersedes the older one silently; the older one is told so it can
 * drop its own toast. The keyboard rules are the board's own: nothing in a field or a dialog, nothing with Shift or
 * Alt, nothing a handler already took.
 */

type Holder = { owner: string; run: () => void; onLost: () => void };

let holder: Holder | null = null;
let listening = false;

const FIELD = 'input, textarea, select, [contenteditable="true"], [contenteditable=""], [role="dialog"]';
const inField = (t: EventTarget | null): boolean => t instanceof HTMLElement && t.closest(FIELD) !== null;

function onKey(event: KeyboardEvent): void {
  if (holder === null) return;
  if (event.key.toLowerCase() !== 'z' || !(event.ctrlKey || event.metaKey) || event.shiftKey || event.altKey) return;
  if (event.defaultPrevented || inField(event.target)) return;
  event.preventDefault();
  holder.run();
}

function listen(on: boolean): void {
  if (on === listening) return;
  listening = on;
  if (on) window.addEventListener('keydown', onKey);
  else window.removeEventListener('keydown', onKey);
}

/** Takes the ⌘Z slot for `owner`; the previous holder's `onLost` runs. `run` is what ⌘Z does now. */
export function claimUndo(owner: string, run: () => void, onLost: () => void): void {
  const previous = holder;
  holder = { owner, run, onLost };
  listen(true);
  if (previous !== null && previous.owner !== owner) previous.onLost();
}

/** Gives the slot back if `owner` still holds it; a no-op otherwise. */
export function releaseUndo(owner: string): void {
  if (holder?.owner !== owner) return;
  holder = null;
  listen(false);
}

/** Who holds the slot (for tests). */
export const undoOwner = (): string | null => holder?.owner ?? null;

/** Drops the holder and the listener (for tests). */
export function resetUndoSlot(): void {
  holder = null;
  listen(false);
}
